import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaMapMarkerAlt,
  FaBuilding,
} from "react-icons/fa";

const LocationManagement = () => {
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city: "",
    country: "",
    postal_code: "",
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/locations`,
        {
          credentials: "include",
        }
      );
      const data = await response.json();
      setLocations(data.locations || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
    setIsLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const url = editingLocation
        ? `${process.env.REACT_APP_BASE_URL}/api/assets/locations/${editingLocation.id}`
        : `${process.env.REACT_APP_BASE_URL}/api/assets/locations`;
      const method = editingLocation ? "PUT" : "POST";
      const body = {
        ...formData,
      };
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (response.ok) {
        alert(
          `Location ${editingLocation ? "updated" : "created"} successfully!`
        );
        setFormData({
          name: "",
          address: "",
          city: "",
          country: "",
          postal_code: "",
        });
        setEditingLocation(null);
        setShowModal(false);
        fetchLocations();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error("Error saving location:", error);
      alert("Error saving location");
    }
    setIsLoading(false);
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address || "",
      city: location.city || "",
      country: location.country || "",
      postal_code: location.postal_code || "",
    });
    setShowModal(true);
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm("Are you sure you want to delete this location?")) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/assets/locations/${locationId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        alert("Location deleted successfully!");
        fetchLocations();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error("Error deleting location:", error);
      alert("Error deleting location");
    }
  };

  const openAddModal = () => {
    setFormData({
      name: "",
      address: "",
      city: "",
      country: "",
      postal_code: "",
    });
    setEditingLocation(null);
    setShowModal(true);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
          Location Management
        </h2>
        <p className="mt-3 text-lg text-slate-600 max-w-2xl mx-auto">
          Manage your business site locations for better organization and asset
          tracking
        </p>
      </div>

      {/* Main Content */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-200/60 shadow-lg">
        <div className="p-8 border-b border-slate-200/60">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600">
                <FaMapMarkerAlt className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-800">
                  Site Locations
                </h3>
                <p className="text-slate-600">
                  {locations.length}{" "}
                  {locations.length === 1 ? "location" : "locations"} configured
                </p>
              </div>
            </div>
            <button
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-lg shadow-blue-500/25 transition-all duration-200"
              onClick={openAddModal}
            >
              <FaPlus className="w-4 h-4" />
              <span>Add Location</span>
            </button>
          </div>
        </div>

        <div className="p-8">
          {isLoading ? (
            <div className="text-center py-8">Loading locations...</div>
          ) : locations.length === 0 ? (
            <div className="text-center py-16">
              <div className="p-6 bg-slate-100 rounded-full w-fit mx-auto mb-6">
                <FaMapMarkerAlt className="w-12 h-12 text-slate-400" />
              </div>
              <h4 className="text-2xl font-bold text-slate-800 mb-4">
                No Locations Found
              </h4>
              <p className="text-slate-600 max-w-md mx-auto leading-relaxed mb-8">
                You haven't created any site locations yet. Add your first
                location to start organizing your assets.
              </p>
              <button
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-lg shadow-blue-500/25 transition-all duration-200"
                onClick={openAddModal}
              >
                Create Your First Location
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {locations.map((location) => (
                <div
                  key={location.id}
                  className="bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <FaBuilding className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-slate-800">
                            {location.name}
                          </h4>
                          <p className="text-sm text-slate-600">
                            {location.city}, {location.country}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(location)}
                          className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(location.id)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="bg-slate-50/80 rounded-lg p-4 space-y-2">
                      {location.address && (
                        <div>
                          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Address
                          </span>
                          <p className="text-sm text-slate-700 mt-1">
                            {location.address}
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4 pt-2">
                        {location.city && (
                          <div>
                            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                              City
                            </span>
                            <p className="text-sm text-slate-700 mt-1">
                              {location.city}
                            </p>
                          </div>
                        )}
                        {location.postal_code && (
                          <div>
                            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                              Postal Code
                            </span>
                            <p className="text-sm text-slate-700 mt-1">
                              {location.postal_code}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingLocation ? "Edit Location" : "Add New Location"}
                </h3>
                <button
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  onClick={() => {
                    setShowModal(false);
                    setEditingLocation(null);
                    setFormData({
                      name: "",
                      address: "",
                      city: "",
                      country: "",
                      postal_code: "",
                    });
                  }}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Location Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Enter location name"
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Address
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                  placeholder="Enter full address"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, city: e.target.value }))
                    }
                    placeholder="Enter city"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        country: e.target.value,
                      }))
                    }
                    placeholder="Enter country"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Postal Code
                </label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      postal_code: e.target.value,
                    }))
                  }
                  placeholder="Enter postal code"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                />
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 font-semibold transition-colors"
                  onClick={() => {
                    setShowModal(false);
                    setEditingLocation(null);
                    setFormData({
                      name: "",
                      address: "",
                      city: "",
                      country: "",
                      postal_code: "",
                    });
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-lg shadow-blue-500/25 transition-all duration-200 disabled:opacity-50"
                >
                  <FaSave className="w-4 h-4" />
                  <span>{editingLocation ? "Update" : "Create"} Location</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationManagement;
